/** Stands in for a first name in prose when the patient has no name on file. */
const NO_NAME_PHRASE = "this patient";

/** Label for a name slot — a heading, a table cell — when there is no name on file. */
export const NO_NAME_LABEL = "Unnamed patient";

/**
 * The patient's name as stored, or `null` when they have no name on file. Blank
 * is no name, and so is a name equal to the patient's own id: patients created
 * before the name write was fixed hold their `external_id` in `user.name`, and
 * an id must never be read out as a person's name.
 */
export function patientDisplayName(
  name: string | null | undefined,
  patientId?: string | null,
): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  if (patientId && trimmed === patientId.trim()) return null;
  return trimmed;
}

/**
 * The patient's first name, or `null` when they have no name on file. Same
 * absent-name rules as {@link patientDisplayName}.
 */
export function patientFirstName(
  name: string | null | undefined,
  patientId?: string | null,
): string | null {
  const displayName = patientDisplayName(name, patientId);
  if (!displayName) return null;
  return displayName.split(/\s+/)[0] ?? null;
}

/** How to refer to the patient mid-sentence: their first name, or "this patient". */
export function patientNameInSentence(firstName: string | null): string {
  return firstName ?? NO_NAME_PHRASE;
}

/** Sentence-opening form of {@link patientNameInSentence}: "Laura", or "This patient". */
export function patientNameStartingSentence(firstName: string | null): string {
  if (firstName) return firstName;
  return NO_NAME_PHRASE.charAt(0).toUpperCase() + NO_NAME_PHRASE.slice(1);
}

/**
 * Longest greeting name that still fits the welcome line on a phone. Measured
 * against "Shobhan Karthick", the longest name that renders on one line there.
 */
const GREETING_NAME_MAX = "Shobhan Karthick".length;

/**
 * The name to greet the patient by: their full name when it fits the line, and
 * their first name alone when it does not. A longer full name wrapped the
 * heading onto a second line and pushed the sign-out control out of the row.
 *
 * @param name the name as stored, or null
 * @param patientId used to reject a name that is really an id
 * @returns the greeting name, or null when there is no name on file
 */
export function patientGreetingName(
  name: string | null | undefined,
  patientId?: string | null,
): string | null {
  const displayName = patientDisplayName(name, patientId);
  if (!displayName) return null;
  if (displayName.length <= GREETING_NAME_MAX) return displayName;
  return patientFirstName(displayName, patientId);
}
